import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';

import useJurisdictions from '@/hooks/useJurisdictions';
import ExternalVerificationRepository from '@/repositories/ExternalVerificationRepository';
import { Badge, Select } from '@/ui';
import { OptionType } from '@/ui/components/Form/Select/types';

import type { LinkFilters } from './urlState';

interface VobListPickersProps {
  linkFilters: LinkFilters;
  onChange: (key: string, values: string[]) => void;
}

const FILTER_OPTIONS_STALE_MS = 60 * 60 * 1000;

const asOptions = (values: string[] = []): OptionType<string>[] =>
  values.map((value) => ({ value, label: value }));

const VobListPickers = ({ linkFilters, onChange }: VobListPickersProps): JSX.Element => {
  const externalVerificationRepo = ExternalVerificationRepository();
  const filterOptions = useQuery({
    queryKey: ['vobListFilterOptions'],
    queryFn: () => externalVerificationRepo.listInsuranceVerificationFilterOptions(),
    staleTime: FILTER_OPTIONS_STALE_MS
  });
  const jurisdictions = useJurisdictions();

  const pickers = [
    {
      key: 'policy_carrier',
      label: 'Carrier',
      width: 'w-72',
      options: filterOptions.data?.carriers
    },
    { key: 'policy_plan', label: 'Plan', width: 'w-96', options: filterOptions.data?.plans },
    {
      key: 'member_jurisdiction',
      label: 'Jurisdiction',
      width: 'w-72',
      options: jurisdictions.data?.map((jurisdiction) => jurisdiction.name)
    }
  ];

  return (
    <>
      {pickers.map(({ key, label, width, options }) => (
        <Badge
          key={key}
          color="secondary"
          count={linkFilters[key]?.length}
          hidden={!linkFilters[key]?.length}>
          <Select
            multiple
            isCompact
            className={width}
            label={label}
            ariaLabel={label}
            showLabel={false}
            placeholder={label}
            options={asOptions(options)}
            selectedOptions={asOptions(linkFilters[key])}
            onChange={(selected) =>
              onChange(
                key,
                selected.map((option) => option.value)
              )
            }
          />
        </Badge>
      ))}
    </>
  );
};

export default VobListPickers;
